const LANGUAGE_KEY = 'i18nextLng'

let claimed: string | null = null
let release: (() => void) | null = null

/**
 * Makes the container the sole writer of `i18nextLng`.
 *
 * Every federated app creates its own i18next instance with the browser
 * language detector's default `caches: ['localStorage']`, so they all write
 * this one key and whichever initialises last wins — silently reverting the
 * user's choice. Writes of any other value are dropped once the container has
 * claimed the key; reads are untouched, so the apps still follow along.
 *
 * Call before i18next init. Returns a restore function for tests.
 */
export function lockLanguageKey(): () => void {
  if (release) return release

  const originalSetItem = localStorage.setItem.bind(localStorage)
  const originalRemoveItem = localStorage.removeItem.bind(localStorage)

  localStorage.setItem = (key: string, value: string) => {
    if (key === LANGUAGE_KEY && claimed !== null && value !== claimed) return
    originalSetItem(key, value)
  }

  localStorage.removeItem = (key: string) => {
    if (key === LANGUAGE_KEY && claimed !== null) return
    originalRemoveItem(key)
  }

  release = () => {
    localStorage.setItem = originalSetItem
    localStorage.removeItem = originalRemoveItem
    claimed = null
    release = null
  }

  return release
}

/** Sets the language the guard will accept, then writes it. */
export function claimLanguage(lng: string): void {
  claimed = lng
  // Goes through the patched setItem so the logger still sees container writes.
  localStorage.setItem(LANGUAGE_KEY, lng)
}
